import React, { useState } from 'react';

import { useTheme } from '../../../theme';
import cn from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../utils';
import { useTranslation } from 'react-i18next';

import { useDeleteOrderMutation } from '@websky/graphql';
import { useToggleable } from '../../../hooks';
import { BaseButton } from '../../../BaseComponents';
import { CdnIcon } from '@shared/ui';
import { ModalX } from '../../../BaseComponents';

interface TriggerRenderProps {
	open: () => void;
}

interface Props {
	orderId: string;
	orderLocator: string;
	onDeleted: React.Dispatch<void>;
	renderTrigger?: (props: TriggerRenderProps) => React.ReactNode;
}

const DeleteOrderModal: React.FC<Props> = ({ orderId, orderLocator, onDeleted, renderTrigger }) => {
	const { t } = useTranslation('OrderCard');
	const { DeleteOrderModal: css } = useTheme('OrderCard');
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const { isOpen, open, close } = useToggleable(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const closeModal = () => {
		setIsDeleting(false);
		close();
	};
	const [deleteOrderMethod] = useDeleteOrderMutation();

	const deleteOrder = async () => {
		setIsDeleting(true);
		try {
			const { data, errors } = await deleteOrderMethod({
				variables: {
					params: orderId
				}
			});
			if (data?.DeleteOrder?.result) {
				setIsDeleting(false);
				onDeleted();
				close();

				return true;
			} else {
				console.error(errors);

				return false;
			}
		} catch (error) {
			console.error(`Probably orderId not found`);
			console.error(error);

			return false;
		}
	};

	return (
		<>
			{renderTrigger?.({ open }) ?? (
				<BaseButton
					variant="smallSecondary"
					className={css.delete_order}
					onClick={e => {
						e.preventDefault();
						open();
					}}
				>
					{t('DeleteFromList')}
				</BaseButton>
			)}
			<ModalX
				size="small"
				isOpen={isOpen}
				onClose={closeModal}
				{...(isMobile ? { bottomDrawer: true } : {})}
				classes={{
					paper: cn(css.paper)
				}}
				title={
					<div className={css.header}>
						<div className={css.header__icon}>
							<CdnIcon icon={isMobile ? 'Action/Remove' : 'Info/Warning'} />
						</div>
						<div className={css.header__text}>{t('BookingRemoval')}</div>
					</div>
				}
				footer={
					<div className={css.footer}>
						<BaseButton
							variant="smallSecondary"
							className={css.button__cancel}
							disabled={isDeleting}
							onClick={closeModal}
						>
							{t('Cancel')}
						</BaseButton>
						<BaseButton
							variant="smallPrimary"
							isLoading={isDeleting}
							className={css.button__confirm}
							onClick={deleteOrder}
						>
							{t('Remove')}
						</BaseButton>
					</div>
				}
			>
				<div className={css.message__text}>{t('BookingRemovalMessage', { orderLocator: orderLocator })}</div>
			</ModalX>
		</>
	);
};

export default DeleteOrderModal;
